"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { SportsbookLogoMark } from "@/components/sportsbook-logos";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import { ScrollFade } from "@/components/odds-screen/live/top-menus";
import { TutorialIconLink } from "@/components/ui/tutorial-icon-link";
import { LEARN_ICON_CLASS } from "@/components/ui/control-styles";
import { useDebouncedValue } from "@/components/optimizer/use-debounced-value";
import { getMobileSortOptions } from "@/components/optimizer/mobile/mobile-sort-options";
import type { PlatformType } from "@/components/optimizer/utils-v2";
import type { TableVariant } from "@/components/optimizer/utils-v2";
import type { MarketScope, SortDirection } from "@/components/optimizer/query-state";
import type { MobileView } from "@/lib/saved-filter-pages";
import type { OrderCue } from "@/hooks/use-order-hold";
import { BoostControl } from "./boost-menu";
import { RaceControl, type RaceSlot } from "./race-panel";
import { isBoostableSlipPlatform, promoBlocksBoost } from "@/components/optimizer/props-slip-breakevens";
import { SwitchRow } from "@/components/odds-screen/live/book-panel";
import { BookPicker } from "./book-picker";
import { CrumbMenu, type CrumbItem } from "./crumb-menu";
import { FiltersButton, HiddenCountButton, LiveControl, SEARCH_ICON, SearchField, isTypingTarget, quietClass } from "./chrome";
import type { LeagueChoice } from "./model";
import { StartTimeMenu, type BreaksChoice } from "./start-time";
import type { GameTime, TimeChoices } from "@/lib/game-date";

function SearchControl({ value, onChange, tall, label }: { value: string; onChange: (value: string) => void; tall: boolean; label: string }) {
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounced = useDebouncedValue(draft, 300);
  const open = !tall || opened || value !== "";
  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (debounced !== value) onChange(debounced); }, [debounced]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (opened) inputRef.current?.focus();
  }, [opened]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey || isTypingTarget(event.target)) return;
      event.preventDefault();
      setOpened(true);
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  if (!open) {
    return (
      <button type="button" onClick={() => setOpened(true)} aria-label={label} className={cn(quietClass(false, tall), "px-0", tall ? "w-9" : "w-8")}>
        {SEARCH_ICON}
      </button>
    );
  }
  return (
    <SearchField
      inputRef={inputRef}
      value={draft}
      onChange={setDraft}
      onBlur={() => { if (draft.trim() === "") setOpened(false); }}
      onKeyDown={(event) => { if (event.key === "Escape") { setDraft(""); onChange(""); setOpened(false); inputRef.current?.blur(); } }}
      label={label}
      className={tall ? "h-9 w-[160px]" : "h-8 w-[170px] min-w-[76px] shrink"}
    />
  );
}

export type SlipsBadge = { readonly text: string; readonly tone: "value" | "primary" };

const MARKET_SCOPES: readonly { readonly id: MarketScope; readonly label: string }[] = [
  { id: "", label: "All Markets" },
  { id: "player", label: "Player Props" },
  { id: "game", label: "Game Lines" },
];

export type SortChoice = { readonly sortColumn: string; readonly direction: SortDirection; readonly label: string };

export type FantasyBarProps = {
  isMobile: boolean;
  book?: PlatformType | null;
  pickedBook?: PlatformType;
  books?: readonly PlatformType[];
  myBooks?: readonly PlatformType[];
  onBook?: (book: PlatformType) => void;
  bookMenu?: React.ReactNode;
  targetMenu?: React.ReactNode;
  selectedLeagues: readonly string[];
  leagues: readonly LeagueChoice[];
  onLeagues: (leagues: readonly string[]) => void;
  time: GameTime;
  times: TimeChoices;
  onTime: (time: GameTime) => void;
  liveChoice?: boolean;
  breaks?: BreaksChoice;
  markets?: { readonly value: MarketScope; readonly onPick: (scope: MarketScope) => void };
  search: string;
  onSearch: (value: string) => void;
  searchLabel?: string;
  liveEnabled: boolean;
  heldAtMs: number | null;
  onToggleLive: () => void;
  order: OrderCue | null;
  filtersCount: number;
  filtersOpen: boolean;
  onFilters: () => void;
  boost?: number;
  boostable?: boolean;
  boostDescription?: string;
  onBoost?: (boost: number) => void;
  stakeControl?: React.ReactNode;
  slips?: { readonly open: boolean; readonly badge: SlipsBadge | null; readonly onOpen: () => void; readonly label?: string };
  race?: RaceSlot;
  sort: { column: string; direction: SortDirection };
  sortVariant?: TableVariant;
  sortOptions?: readonly SortChoice[];
  onSort: (column: string, direction: SortDirection) => void;
  mobileView?: MobileView;
  hiddenCount: number;
  onHidden: () => void;
  tutorial?: { videoUrl: string; toolLabel: string; toolId?: string };
};

export function FantasyBar({
  isMobile, book, pickedBook, books, myBooks, onBook, bookMenu: bookMenuSlot, targetMenu, selectedLeagues, leagues, onLeagues, time, times, onTime, liveChoice = false, breaks, markets, search, onSearch, searchLabel = "Search players",
  liveEnabled, heldAtMs, onToggleLive, order, filtersCount, filtersOpen, onFilters, boost = 0, boostable = false, boostDescription: boostDescriptionProp, onBoost, stakeControl, slips, sort, sortVariant = "props", sortOptions: sortChoices, onSort, race,
  mobileView, hiddenCount, onHidden, tutorial,
}: FantasyBarProps) {
  const allLeagues = selectedLeagues.length === 0;
  const leagueItems: CrumbItem[] = leagues.map((item) => ({ id: item.id, label: item.label, meta: item.props === null ? undefined : String(item.props), active: item.active }));
  const sortOptions = sortChoices ?? getMobileSortOptions(sortVariant);
  const sortItems: CrumbItem[] = sortOptions.map((option) => ({
    id: `${option.sortColumn}:${option.direction}`, label: option.label, active: option.sortColumn === sort.column,
  }));
  const chosenLeagues = leagues.filter((item) => item.active);
  const leagueTriggerLabel = allLeagues
    ? "All Leagues"
    : chosenLeagues.length === 0
      ? selectedLeagues[0].toUpperCase()
      : chosenLeagues.length === 1
        ? chosenLeagues[0].label
        : `${chosenLeagues[0].label} +${chosenLeagues.length - 1}`;
  const pickLeague = (id: string) => {
    const next = new Set(selectedLeagues);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onLeagues(leagues.map((item) => item.id).filter((league) => next.has(league)));
  };
  const bookMenu = bookMenuSlot ?? (book !== undefined && pickedBook !== undefined && books !== undefined && onBook !== undefined
    ? <BookPicker book={book} picked={pickedBook} books={books} myBooks={myBooks} isMobile={isMobile} onPick={onBook} />
    : null);
  const allLeaguesSwitch = (
    <div className="mb-1 border-b border-white/[0.08] pb-1">
      <SwitchRow
        checked={allLeagues}
        title="All Leagues"
        sheet={isMobile}
        onToggle={() => onLeagues(allLeagues ? leagues.map((item) => item.id) : [])}
      />
    </div>
  );
  const leagueMenu = (
    <CrumbMenu title="League" label={leagueTriggerLabel} items={leagueItems} isMobile={isMobile} multiple header={allLeaguesSwitch} onPick={pickLeague} />
  );
  const dayMenu = <StartTimeMenu time={time} times={times} isMobile={isMobile} liveChoice={liveChoice} breaks={breaks} onTime={onTime} />;
  const marketItems: CrumbItem[] = MARKET_SCOPES.map((scope) => ({ id: scope.id, label: scope.label, active: scope.id === markets?.value }));
  const marketMenu = markets ? (
    <CrumbMenu title="Markets" label={MARKET_SCOPES.find((scope) => scope.id === markets.value)?.label ?? "All Markets"} items={marketItems} isMobile={isMobile} tone="quiet" className="shrink-0" onPick={(id) => markets.onPick(id as MarketScope)} />
  ) : null;
  const liveControl = <LiveControl enabled={liveEnabled} heldAtMs={heldAtMs} onToggle={onToggleLive} order={order} tall={isMobile} />;
  const filters = <FiltersButton count={filtersCount} open={filtersOpen} onClick={onFilters} tall={isMobile} />;
  const boostDescription = boostDescriptionProp ?? (book != null && promoBlocksBoost(book)
    ? `Winnings boost at ${sportsbookIdToDisplay(book)}. It scales the whole payout of a winning slip, so the odds to hit you need drops to match. Slips with a promo pick can't take it.`
    : book == null || isBoostableSlipPlatform(book)
      ? "Winnings boost on your slip. Every payout is scaled by it, so the odds to hit you need drops to match."
      : `Profit boost at ${sportsbookIdToDisplay(book)}. Winnings are scaled by it, so EV is re-rated to match.`);
  const boostControl = boostable && onBoost !== undefined ? <BoostControl value={boost} onChange={onBoost} isMobile={isMobile} description={boostDescription} /> : null;
  const slipsButton = slips ? (
    <button
      type="button"
      onClick={slips.onOpen}
      aria-expanded={slips.open}
      aria-label={slips.badge ? `${slips.label ?? "+EV Slips"}, ${slips.badge.text}` : slips.label ?? "+EV Slips"}
      className={quietClass(slips.open, isMobile)}
    >
      <span>{slips.label ?? "+EV Slips"}</span>
      {slips.badge && (
        <span className={cn("data-num rounded px-1 text-[11px] font-semibold leading-[18px]", slips.badge.tone === "value" ? "bg-accent-value/15 text-accent-value" : "bg-primary/15 text-primary")}>{slips.badge.text}</span>
      )}
    </button>
  ) : null;
  const raceControl = race ? <RaceControl {...race} isMobile={isMobile} /> : null;
  const hidden = <HiddenCountButton count={hiddenCount} noun="bet" onClick={onHidden} tall={isMobile} />;
  const learn = tutorial ? <TutorialIconLink videoUrl={tutorial.videoUrl} toolLabel={tutorial.toolLabel} toolId={tutorial.toolId} className={cn(LEARN_ICON_CLASS, isMobile && "h-9")} /> : null;

  if (isMobile) {
    return (
      <div className="flex-shrink-0 bg-surface-raised" data-tour="filter-bar">
        <div className="flex h-12 items-center gap-1.5 border-b border-white/[0.08] px-3">
          {bookMenu}
          <div className="flex min-w-0 flex-1">{leagueMenu}</div>
          {learn}
          {filters}
        </div>
        <ScrollFade className="scrollbar-hide flex h-12 items-center gap-2 overflow-x-auto border-b border-white/[0.08] px-3">
          {targetMenu}
          {dayMenu}
          {marketMenu}
          <SearchControl value={search} onChange={onSearch} tall label={searchLabel} />
          {liveControl}
          {raceControl}
          {boostControl}
          {stakeControl}
          {sortOptions.length > 1 && mobileView !== "table" && (
            <CrumbMenu
              title="Sort"
              label={sortOptions.find((option) => option.sortColumn === sort.column)?.label ?? "Sort"}
              items={sortItems}
              isMobile
              tone="quiet"
              className="shrink-0"
              onPick={(id) => {
                const [column, direction] = id.split(":");
                onSort(column, direction as SortDirection);
              }}
            />
          )}
          {hidden}
        </ScrollFade>
      </div>
    );
  }

  return (
    <div className="flex min-h-11 flex-shrink-0 flex-wrap items-center gap-x-1 border-b border-surface-line px-3" data-tour="filter-bar">
      <div className="flex min-h-11 min-w-0 flex-wrap items-center gap-1.5 py-1.5">
        {bookMenu}
        {targetMenu}
        {leagueMenu}
        {dayMenu}
        {marketMenu}
      </div>
      <div className="flex h-11 min-w-0 items-center gap-2.5 pl-2">
        <SearchControl value={search} onChange={onSearch} tall={false} label={searchLabel} />
        {hidden}
      </div>
      <div className="ml-auto flex h-11 items-center gap-1">
        {liveControl}
        {slipsButton}
        {raceControl}
        {boostControl}
        {stakeControl}
        {filters}
        {learn}
      </div>
    </div>
  );
}
